import { useRef } from 'react'
import { Trans, useTranslation } from 'next-i18next'

import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { ButtonPrimary, ButtonSecondary } from '~/components/Buttons'

interface Props {
  cancelOnClick: () => void
  continueOnClick: () => Promise<void> | void
  i18nKey: string
  i18nNamespace: string
  isChangeLoading?: boolean
  isSmallPopup?: boolean
}

export default function WarningModal({
  cancelOnClick,
  continueOnClick,
  i18nKey,
  i18nNamespace,
  isChangeLoading,
  isSmallPopup,
}: Props): React.ReactElement {
  const modalRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation([i18nNamespace, 'generics'])

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      cancelOnClick()
    }
  }

  return (
    <aside className="fixed z-top inset-0 bg-black/70">
      <section className="h-full grid items-center justify-center" onClick={handleOutsideClick}>
        <div
          className={`z-10 absolute rounded bg-white border-purple top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 border-2 ${
            isSmallPopup ? 'w-85 sm:w-96 lg:w-120' : 'w-85 md:w-1/2 lg:w-5/12 xl:w-1/3'
          } pb-2`}
          ref={modalRef}
        >
          <div className="w-full flex items-center justify-between px-3 pt-2.5">
            <p className="text-white text-center text-xl font-bold bg-red rounded-4 w-6 h-6.5">!</p>
            <button className="md:-mt-2" onClick={cancelOnClick}>
              <ResponsiveImage height={30} imageClassName="w-6 md:w-7" srcKey="icons/menu-close.png" width={28} />
            </button>
          </div>
          <div
            className={`px-5 md:px-13 text-purple tracking-btn-wide leading-8 pb-6 ${
              isSmallPopup ? 'text-start' : 'text-center'
            }`}
          >
            <Trans
              components={{ bold: <span className="font-bold" />, br: <br /> }}
              i18nKey={i18nKey}
              values={{ warningText: t(i18nKey) }}
            />
          </div>
          <div
            className={`flex  items-center justify-center pb-5  mx-auto text-xs ${
              isSmallPopup ? 'px-4 md:px-12 gap-4 md:gap-8' : 'space-y-3.5 flex-col'
            }`}
          >
            <ButtonPrimary className="w-67 h-12" isLoading={isChangeLoading} onClick={continueOnClick}>
              {t('generics:continue')}
            </ButtonPrimary>
            <ButtonSecondary
              className="w-67 h-12 border-purple border-2 text-purple text-xs"
              isLoading={isChangeLoading}
              onClick={cancelOnClick}
            >
              {t('generics:cancel')}
            </ButtonSecondary>
          </div>
        </div>
      </section>
    </aside>
  )
}
